import React, { useEffect, useState, useRef } from 'react';
import './App.css';
import MeteoMap from './components/MeteoMap';
import MapSearch from './components/MapSearch';
import { LocationData } from './types';
import L from 'leaflet';
import FilterPanel, { FilterState } from './components/FilterPanel';
import Legend, { FilterState as LegendFilterState } from './components/Legend';
import ComparisonPanel from './components/ComparisonPanel';
import { assetUrl } from './utils/asset';
// import { fetchCsvSeries } from './utils/csv';

// Responsive hook for screen size
function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
}

const getInitialFilterState = (): FilterState => ({
  filters: {
    temperature: { type: 'temperature', minValue: null, maxValue: null, active: false },
    pressure: { type: 'pressure', minValue: null, maxValue: null, active: false },
    humidity: { type: 'humidity', minValue: null, maxValue: null, active: false },
    precipitation: { type: 'precipitation', minValue: null, maxValue: null, active: false },
    SM: { type: 'SM', minValue: null, maxValue: null, active: false },
    SMRZ: { type: 'SMRZ', minValue: null, maxValue: null, active: false },
  },
  activeFilters: []
});

const App: React.FC = () => {
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [selected, setSelected] = useState<{ city: string; param: string } | null>(null);
  const [modal, setModal] = useState<{ city: string } | null>(null);
  const mapRef = useRef<L.Map>(null!);
  const isMobile = useIsMobile();
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);
  const [comparisonSidebarOpen, setComparisonSidebarOpen] = useState(false);
  const [comparisonFullscreen, setComparisonFullscreen] = useState(false);
  const [activeCompareCity, setActiveCompareCity] = useState<string | null>(null);
  const [compareCities, setCompareCities] = useState<string[]>([]);
  const [filterState, setFilterState] = useState<FilterState>(getInitialFilterState());
  const isDesktop = !isMobile && window.innerWidth > 700;

  // Toasts
  const [toasts, setToasts] = useState<{ id: number; message: string; type: 'info' | 'success' | 'error' }[]>([]);
  const showToast = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 2400);
  };

  useEffect(() => {
    // Load base locations and remove the three seeded points; we won't preload CSVs anymore
    fetch(assetUrl('/point_data_processed.json'))
      .then(res => res.json())
      .then((baseLocs: LocationData[]) => {
        const filtered = baseLocs.filter(l => !['IISc Bangalore', 'IIT Kanpur', 'IIT Tirupati'].includes(l.name));
        setLocations(filtered);
  // Ensure no lingering selection on refresh
  setSelected(null);
  setModal(null);
  setActiveCompareCity(null);
  setCompareCities([]);
  setComparisonSidebarOpen(false);
  setComparisonFullscreen(false);
      });
  }, []);

  // Handler for MapSearch selection
  const handleSearchSelect = (loc: LocationData) => {
    setSelected(null);
    setModal({ city: loc.name });
    if (mapRef.current) {
      mapRef.current.flyTo([loc.lat, loc.lon], 9, { animate: true, duration: 1 });
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-left">
          <div className="logo-container">
            <img src={assetUrl('/IITT_InIf_svg2-01.webp')} alt="IITT Logo" className="logo-image" />
          </div>
        </div>
        <div className="header-right">
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <h1 style={{ 
              margin: 0,
              color: '#fff',
              textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
              flex: isMobile ? '1 1 100%' : 'initial'
            }}>
              {isMobile ? 'Reflectometry' : 'GNSS Reflectometry tool'}
            </h1>
            <div style={{ 
              fontSize: isMobile ? 14 : 16, 
              fontWeight: 500, 
              color: '#fff', 
              letterSpacing: '0.5px',
              padding: '4px 12px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
              whiteSpace: 'nowrap',
              marginLeft: isMobile ? 0 : 'auto'
            }}>
              Powered by Geo - Intel Lab 
            </div>
          </div>
        </div>
      </header>
      <main className="App-content">
        <div className="map-search-bar">
          <MapSearch locations={locations} onSelect={handleSearchSelect} />
        </div>
        <div className="map-container">
          <MeteoMap 
            locations={locations} 
            selected={selected}
            setSelected={setSelected}
            modal={modal}
            setModal={setModal}
            mapRef={mapRef}
            filterState={filterState}
            onAddLocation={(loc) => {
              setLocations(prev => {
                const exists = prev.find(p => Math.abs(p.lat - loc.lat) < 1e-6 && Math.abs(p.lon - loc.lon) < 1e-6);
                return exists ? prev : [...prev, loc];
              });
              setActiveCompareCity(loc.name);
              setCompareCities(prev => prev.includes(loc.name) ? prev : [...prev, loc.name]);
            }}
            onOpenComparisonSidebar={() => setComparisonSidebarOpen(true)}
            onShowToast={showToast}
            onIndexVisibilityChange={(visible:boolean) => {
              // Toggle a body class so CSS can react to index visibility
              const cls = 'sm-index-visible';
              if (visible) document.body.classList.add(cls);
              else document.body.classList.remove(cls);
            }}
          />
        </div>
        {/* Floating Filter Button */}
        <button 
          className={`filter-fab${filterSidebarOpen || comparisonSidebarOpen ? ' hide' : ''}`} 
          onClick={() => setFilterSidebarOpen(true)} 
          title="Filter Data"
        >
          <span className="filter-fab-icon" />
        </button>
        {/* Floating Comparison Button */}
        <button 
          className={`comparison-fab${comparisonSidebarOpen || filterSidebarOpen ? ' hide' : ''}`} 
          onClick={() => setComparisonSidebarOpen(true)} 
          title="Compare States"
        >
          <span className="comparison-fab-icon" />
        </button>
        {/* Floating Legend (desktop only, when filter active and sidebar closed) */}
        {isDesktop && !filterSidebarOpen && filterState.activeFilters.length > 0 && (
          <div className="floating-legend">
            <Legend filter={filterState as LegendFilterState} />
          </div>
        )}
        {/* Filter Sidebar and Backdrop */}
        {filterSidebarOpen && (
          <>
            <div className="filter-sidebar-backdrop" onClick={() => setFilterSidebarOpen(false)} />
            <aside className={`filter-sidebar open`}>
              <FilterPanel
                filter={filterState}
                onFilterChange={setFilterState}
                onClose={() => setFilterSidebarOpen(false)}
              />
            </aside>
          </>
        )}
        {/* Comparison Sidebar and Backdrop */}
        {comparisonSidebarOpen && (
          <>
            <div className="filter-sidebar-backdrop" onClick={() => setComparisonSidebarOpen(false)} />
            <aside className={`filter-sidebar open`}>
              <ComparisonPanel
                locations={locations}
                activeCity={activeCompareCity || undefined}
                selectedCities={compareCities}
                onClose={() => setComparisonSidebarOpen(false)}
                onFullscreenChange={setComparisonFullscreen}
                isFullscreen={comparisonFullscreen}
              />
            </aside>
          </>
        )}
        {/* Fullscreen Comparison Panel */}
        {comparisonFullscreen && (
          <div className="comparison-fullscreen-container">
            <ComparisonPanel
              locations={locations}
              activeCity={activeCompareCity || undefined}
              selectedCities={compareCities}
              onClose={() => setComparisonFullscreen(false)}
              onFullscreenChange={setComparisonFullscreen}
              isFullscreen={true}
            />
          </div>
        )}
        {/* Toasts */}
        {toasts.length > 0 && (
          <div className="toast-container">
            {toasts.map(t => (
              <div key={t.id} className={`toast ${t.type}`}>
                <div className="toast-title">{t.type === 'success' ? 'Success' : t.type === 'error' ? 'Error' : 'Notice'}</div>
                <div className="toast-msg">{t.message}</div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default App;
